import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { X, Smartphone, Mail, AlertCircle, ArrowLeft } from "lucide-react";
import { AuthContext } from "../../Hook/AuthProvider";
import {
  googleLoginApi,
  sendMobileOtp,
  verifyMobileOtp,
  sendEmailOtp,
  verifyEmailOtp,
} from "../../services/authenticationServices";
import mailchecker from "../../utils/mailchecker";

const modalStyles = `
  .lm-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background:
      radial-gradient(900px 500px at 50% -15%, rgba(43, 169, 212, 0.28), transparent 65%),
      rgba(15, 23, 42, 0.62);
    -webkit-backdrop-filter: blur(10px) saturate(120%);
    backdrop-filter: blur(10px) saturate(120%);
    font-family: 'Source Sans 3', sans-serif;
    animation: lmOverlayIn 0.2s ease-out;
  }

  .lm-card {
    position: relative;
    width: 100%;
    max-width: 440px;
    padding: 32px;
    background: #ffffff;
    border: 1px solid rgba(15, 23, 42, 0.08);
    border-radius: 24px;
    box-shadow:
      0 32px 64px -16px rgba(15, 23, 42, 0.42),
      0 12px 24px -12px rgba(15, 23, 42, 0.16);
    animation: lmCardIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .lm-close {
    position: absolute;
    top: 16px;
    right: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    color: #64748B;
    background: #F1F5F9;
    border: 0;
    border-radius: 50%;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, transform 0.15s ease;
  }

  .lm-close:hover {
    background: #E2E8F0;
    color: #0F172A;
    transform: rotate(90deg);
  }

  .lm-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: 22px;
  }

  .lm-brand {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 9px 16px;
    margin-bottom: 14px;
    background: linear-gradient(135deg, #EFF6FF, #ECFEFF);
    border: 1px solid #DBEAFE;
    border-radius: 14px;
  }

  .lm-brand img {
    display: block;
    height: 22px;
    width: auto;
  }

  .lm-title {
    margin: 0 0 6px;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: #0F172A;
  }

  .lm-subtitle {
    margin: 0;
    font-size: 14px;
    line-height: 1.4;
    color: #64748B;
  }

  .lm-subtitle strong {
    color: #0F172A;
    font-weight: 600;
  }

  .lm-google {
    display: flex;
    justify-content: center;
  }

  .lm-google-loading {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    max-width: 370px;
    height: 44px;
    padding: 0 16px;
    font-size: 14px;
    font-weight: 600;
    color: #1E293B;
    background: #F8FAFC;
    border: 1px solid #CBD5E1;
    border-radius: 9999px;
    box-sizing: border-box;
  }

  .lm-google-loading .lm-spinner {
    border-color: rgba(30, 41, 59, 0.2);
    border-top-color: #0056D2;
  }

  .lm-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 18px 0;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #94A3B8;
  }

  .lm-divider::before,
  .lm-divider::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #E2E8F0;
  }

  .lm-tabs {
    display: flex;
    gap: 4px;
    padding: 4px;
    margin-bottom: 18px;
    background: #F1F5F9;
    border: 1px solid #E2E8F0;
    border-radius: 13px;
  }

  .lm-tab {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 9px 0;
    font-family: inherit;
    font-size: 13.5px;
    font-weight: 600;
    color: #64748B;
    background: transparent;
    border: 0;
    border-radius: 9px;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  }

  .lm-tab:hover {
    color: #0F172A;
  }

  .lm-tab.is-active {
    color: #0056D2;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08), 0 2px 8px -2px rgba(15, 23, 42, 0.12);
  }

  .lm-error {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 12px;
    margin-bottom: 14px;
    font-size: 13px;
    line-height: 1.35;
    color: #B91C1C;
    background: #FEF2F2;
    border: 1px solid #FECACA;
    border-radius: 12px;
    animation: lmShake 0.3s ease;
  }

  .lm-error svg {
    flex-shrink: 0;
  }

  .lm-field {
    position: relative;
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 54px;
    padding: 0 14px;
    margin-bottom: 20px;
    background: #F8FAFC;
    border: 1.5px solid #E2E8F0;
    border-radius: 14px;
    box-sizing: border-box;
    transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  }

  .lm-field.is-floating {
    background: #ffffff;
  }

  .lm-field.is-focused {
    background: #ffffff;
    border-color: #0056D2;
    box-shadow: 0 0 0 4px rgba(0, 86, 210, 0.1);
  }

  .lm-field > svg {
    flex-shrink: 0;
    color: #94A3B8;
    transition: color 0.15s ease;
  }

  .lm-field.is-focused > svg {
    color: #0056D2;
  }

  .lm-label {
    position: absolute;
    left: 42px;
    top: 50%;
    padding: 0 5px;
    font-size: 15px;
    color: #94A3B8;
    background: transparent;
    pointer-events: none;
    transform: translateY(-50%);
    transition: top 0.15s ease, font-size 0.15s ease, color 0.15s ease, background 0.15s ease;
  }

  .lm-field.is-floating .lm-label {
    top: 0;
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.02em;
    color: #64748B;
    background: #ffffff;
  }

  .lm-field.is-focused .lm-label {
    color: #0056D2;
  }

  .lm-input {
    flex: 1;
    min-width: 0;
    width: 100%;
    padding: 0;
    font-family: inherit;
    font-size: 15px;
    color: #0F172A;
    background: transparent;
    border: 0;
    outline: none;
  }

  .lm-input::placeholder {
    color: #94A3B8;
  }

  .lm-prefix {
    flex-shrink: 0;
    font-size: 15px;
    font-weight: 600;
    color: #0F172A;
  }

  .lm-prefix-divider {
    flex-shrink: 0;
    width: 1px;
    height: 18px;
    background: #E2E8F0;
  }

  .lm-primary {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    min-height: 50px;
    padding: 12px 16px;
    font-family: inherit;
    font-size: 15px;
    font-weight: 600;
    color: #ffffff;
    background: linear-gradient(135deg, #0056D2, #2BA9D4);
    border: 0;
    border-radius: 14px;
    cursor: pointer;
    box-shadow: 0 8px 20px -8px rgba(0, 86, 210, 0.6);
    transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
  }

  .lm-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 12px 24px -8px rgba(0, 86, 210, 0.65);
  }

  .lm-primary:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 4px 12px -6px rgba(0, 86, 210, 0.5);
  }

  .lm-primary:disabled {
    color: #94A3B8;
    background: #E2E8F0;
    box-shadow: none;
    cursor: not-allowed;
  }

  .lm-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.45);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: lmSpin 0.7s linear infinite;
  }

  .lm-primary:disabled .lm-spinner {
    border-color: rgba(148, 163, 184, 0.45);
    border-top-color: #64748B;
  }

  .lm-otp {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 18px;
  }

  .lm-otp input {
    width: 100%;
    max-width: 48px;
    height: 54px;
    font-family: inherit;
    font-size: 20px;
    font-weight: 700;
    text-align: center;
    color: #0F172A;
    background: #F8FAFC;
    border: 1.5px solid #E2E8F0;
    border-radius: 12px;
    outline: none;
    transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
  }

  .lm-otp input:focus {
    background: #ffffff;
    border-color: #0056D2;
    box-shadow: 0 0 0 4px rgba(0, 86, 210, 0.1);
  }

  .lm-otp input:not(:placeholder-shown) {
    color: #0056D2;
    border-color: #BFDBFE;
  }

  .lm-otp-error {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    min-height: 50px;
    padding: 12px;
    margin-bottom: 12px;
    font-size: 14px;
    font-weight: 600;
    text-align: center;
    color: #B91C1C;
    background: #FEF2F2;
    border: 1.5px solid #FECACA;
    border-radius: 14px;
    box-sizing: border-box;
    animation: lmShake 0.3s ease;
  }

  .lm-otp-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 4px;
  }

  .lm-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: #0056D2;
    background: none;
    border: 0;
    cursor: pointer;
    transition: color 0.15s ease;
  }

  .lm-link:hover {
    color: #003EA8;
  }

  .lm-muted {
    font-size: 13px;
    font-weight: 500;
    color: #64748B;
  }

  .lm-footer {
    margin-top: 20px;
    text-align: center;
  }

  .lm-footer button {
    padding: 0;
    font-family: inherit;
    font-size: 13px;
    font-weight: 500;
    color: #94A3B8;
    background: none;
    border: 0;
    cursor: pointer;
    transition: color 0.15s ease;
  }

  .lm-footer button:hover {
    color: #475569;
  }

  @keyframes lmOverlayIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes lmCardIn {
    from { opacity: 0; transform: translateY(14px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  @keyframes lmSpin {
    to { transform: rotate(360deg); }
  }

  @keyframes lmShake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-4px); }
    75% { transform: translateX(4px); }
  }

  @media (max-width: 420px) {
    .lm-card {
      padding: 26px 20px;
      border-radius: 20px;
    }

    .lm-title {
      font-size: 20px;
    }

    .lm-otp input {
      height: 50px;
      border-radius: 11px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .lm-overlay,
    .lm-card,
    .lm-error,
    .lm-otp-error,
    .lm-spinner {
      animation: none;
    }
  }
`;

const LoginModal = ({ isOpen, onClose, canDismiss = true, onLoginSuccess }) => {
  const { loginSuccess } = useContext(AuthContext);

  // Auth mode: 'phone' or 'email'
  const [authMode, setAuthMode] = useState("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [isPhoneFocused, setIsPhoneFocused] = useState(false);
  const [isEmailFocused, setIsEmailFocused] = useState(false);
  const phoneInputRef = useRef(null);
  const emailInputRef = useRef(null);

  // OTP state
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timer, setTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);

  const [googleLoading, setGoogleLoading] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const isAnyLoading = googleLoading || otpSending || otpVerifying;

  const [errorMessage, setErrorMessage] = useState("");

  // Auto-focus input whenever modal opens or tab switches
  useEffect(() => {
    if (isOpen && !otpStep) {
      const timerId = setTimeout(() => {
        if (authMode === "phone") {
          phoneInputRef.current?.focus();
        } else {
          emailInputRef.current?.focus();
        }
      }, 50);
      return () => clearTimeout(timerId);
    }
  }, [isOpen, authMode, otpStep]);

  const isPhoneValid = useMemo(() => {
    const cleaned = phoneNumber.trim().replace(/\D/g, "");
    return cleaned.length === 10 && /^[6-9]\d{9}$/.test(cleaned);
  }, [phoneNumber]);

  const isEmailValid = useMemo(() => {
    const trimmed = email.trim();
    return trimmed.length > 3 && mailchecker.isValid(trimmed);
  }, [email]);

  const canSendOtp = authMode === "phone" ? isPhoneValid : isEmailValid;

  const isOtpComplete = useMemo(() => {
    return otp.every((d) => d && d.trim().length === 1) && otp.join("").length === 6;
  }, [otp]);

  // Timer countdown
  useEffect(() => {
    let interval = null;
    if (otpStep && timer > 0) {
      interval = setInterval(() => setTimer((t) => t - 1), 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [otpStep, timer]);

  if (!isOpen) return null;

  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    setErrorMessage("");
    try {
      const response = await googleLoginApi(credentialResponse.credential);
      await loginSuccess(response);
      if (onLoginSuccess) onLoginSuccess(response);
      onClose();
    } catch (err) {
      console.error("Google login error:", err);
      setErrorMessage(
        err.response?.data?.detail || "Google login failed. Please try again."
      );
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    setErrorMessage("");

    if (authMode === "phone") {
      const indianMobileRegex = /^(?:(?:\+|0{1,2})91[\s-]*)?[6-9]\d{9}$/;
      const cleaned = phoneNumber.trim().replace(/[\s-]/g, "");
      if (!indianMobileRegex.test(cleaned)) {
        setErrorMessage("Please enter a valid number");
        return;
      }
      setOtpSending(true);
      try {
        const numOnly = cleaned.slice(-10);
        await sendMobileOtp(numOnly);
        setOtpStep(true);
        setTimer(30);
        setCanResend(false);
        setOtp(["", "", "", "", "", ""]);
      } catch (err) {
        console.error("Mobile OTP send error:", err);
        setErrorMessage(
          err.response?.data?.detail ||
          (!err.response || err.message === "Network Error"
            ? "Server unreachable. Please verify server status."
            : "Failed to send mobile OTP. Please try again.")
        );
      } finally {
        setOtpSending(false);
      }
    } else {
      const trimmedEmail = email.trim();
      if (!mailchecker.isValid(trimmedEmail)) {
        setErrorMessage("Invalid Email");
        return;
      }
      setOtpSending(true);
      try {
        await sendEmailOtp(trimmedEmail);
        setOtpStep(true);
        setTimer(30);
        setCanResend(false);
        setOtp(["", "", "", "", "", ""]);
      } catch (err) {
        console.error("Email OTP send error:", err);
        setErrorMessage(
          err.response?.data?.detail ||
          (!err.response || err.message === "Network Error"
            ? "Server unreachable. Please verify server status."
            : "Failed to send email OTP. Please try again.")
        );
      } finally {
        setOtpSending(false);
      }
    }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      setErrorMessage("Please enter the complete 6-digit OTP");
      return;
    }

    setOtpVerifying(true);
    setErrorMessage("");
    try {
      let res;
      if (authMode === "phone") {
        const cleaned = phoneNumber.replace(/\D/g, "");
        const numOnly = cleaned.slice(-10);
        res = await verifyMobileOtp(numOnly, otpCode);
      } else {
        res = await verifyEmailOtp(email.trim(), otpCode);
      }
      await loginSuccess(res);
      if (onLoginSuccess) onLoginSuccess(res);
      onClose();
    } catch (err) {
      console.error("OTP verification error:", err);
      setErrorMessage(
        err.response?.data?.detail || "Invalid OTP. Please try again."
      );
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleOtpInput = (index, value) => {
    if (errorMessage) setErrorMessage("");
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const nextOtp = [...otp];
      nextOtp[index] = value;
      setOtp(nextOtp);

      if (value && index < 5) {
        const nextElem = document.getElementById(`modal-otp-${index + 1}`);
        if (nextElem) nextElem.focus();
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (errorMessage) setErrorMessage("");
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevElem = document.getElementById(`modal-otp-${index - 1}`);
      if (prevElem) prevElem.focus();
    }
  };

  const handleOtpPaste = (e) => {
    if (errorMessage) setErrorMessage("");
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").trim().replace(/\D/g, "");
    if (!pasted) return;
    const digits = pasted.slice(0, 6).split("");
    const nextOtp = ["", "", "", "", "", ""];
    digits.forEach((d, i) => {
      if (i < 6) nextOtp[i] = d;
    });
    setOtp(nextOtp);
    const targetIdx = Math.min(digits.length, 5);
    const targetElem = document.getElementById(`modal-otp-${targetIdx}`);
    if (targetElem) targetElem.focus();
  };

  const destination = authMode === "phone" ? `+91 ${phoneNumber}` : email;

  return (
    <>
      <style>{modalStyles}</style>
      <div
        className="lm-overlay"
        onClick={(e) => {
          if (e.target === e.currentTarget && canDismiss) onClose();
        }}
      >
        <div
          className="lm-card"
          role="dialog"
          aria-modal="true"
          aria-label="Sign in"
        >
          {canDismiss && (
            <button
              type="button"
              className="lm-close"
              onClick={onClose}
              aria-label="Close modal"
            >
              <X size={17} strokeWidth={2.2} />
            </button>
          )}

          <div className="lm-header">
            <div className="lm-brand">
              <img src="/assets/images/logo/SVG/Logo-Org.svg" alt="Yooki" />
            </div>
            <h2 className="lm-title">
              {otpStep ? "Enter verification code" : "Welcome back"}
            </h2>
            <p className="lm-subtitle">
              {otpStep ? (
                <>
                  We sent a 6-digit code to <strong>{destination}</strong>
                </>
              ) : (
                "Sign in to continue your learning journey"
              )}
            </p>
          </div>

          {/* Google Login */}
          <div className="lm-google">
            {googleLoading ? (
              <div className="lm-google-loading">
                <span className="lm-spinner" />
                <span>Signing in with Google...</span>
              </div>
            ) : (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setErrorMessage("Google Sign-In failed")}
                useOneTap={false}
                shape="pill"
                text="continue_with"
                size="large"
                width="370"
              />
            )}
          </div>

          {/* Divider */}
          <div className="lm-divider">or</div>

          {/* Tab Switcher */}
          {!otpStep && (
            <div className="lm-tabs">
              <button
                type="button"
                className={`lm-tab${authMode === "phone" ? " is-active" : ""}`}
                onClick={() => {
                  setAuthMode("phone");
                  setErrorMessage("");
                  setTimeout(() => phoneInputRef.current?.focus(), 20);
                }}
              >
                <Smartphone size={16} strokeWidth={2.2} />
                Mobile
              </button>
              <button
                type="button"
                className={`lm-tab${authMode === "email" ? " is-active" : ""}`}
                onClick={() => {
                  setAuthMode("email");
                  setErrorMessage("");
                  setTimeout(() => emailInputRef.current?.focus(), 20);
                }}
              >
                <Mail size={16} strokeWidth={2.2} />
                Email
              </button>
            </div>
          )}

          {!otpStep && errorMessage && (
            <div className="lm-error">
              <AlertCircle size={16} strokeWidth={2.2} />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Input Form */}
          {!otpStep ? (
            <form onSubmit={handleSendOtp} noValidate>
              {authMode === "phone" ? (
                <div
                  className={`lm-field${isPhoneFocused ? " is-focused" : ""}${
                    isPhoneFocused || phoneNumber ? " is-floating" : ""
                  }`}
                  onClick={() => phoneInputRef.current?.focus()}
                >
                  <Smartphone size={18} strokeWidth={2} />
                  <label className="lm-label">Mobile number</label>
                  {isPhoneFocused || phoneNumber ? (
                    <>
                      <span className="lm-prefix">+91</span>
                      <span className="lm-prefix-divider" />
                    </>
                  ) : null}
                  <input
                    ref={phoneInputRef}
                    className="lm-input"
                    type="tel"
                    placeholder={isPhoneFocused ? "Enter 10-digit number" : ""}
                    maxLength={10}
                    value={phoneNumber}
                    onFocus={() => setIsPhoneFocused(true)}
                    onBlur={() => setIsPhoneFocused(false)}
                    onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ""))}
                  />
                </div>
              ) : (
                <div
                  className={`lm-field${isEmailFocused ? " is-focused" : ""}${
                    isEmailFocused || email ? " is-floating" : ""
                  }`}
                  onClick={() => emailInputRef.current?.focus()}
                >
                  <Mail size={18} strokeWidth={2} />
                  <label className="lm-label">Email Address</label>
                  <input
                    ref={emailInputRef}
                    className="lm-input"
                    type="email"
                    placeholder={isEmailFocused ? "name@example.com" : ""}
                    value={email}
                    onFocus={() => setIsEmailFocused(true)}
                    onBlur={() => setIsEmailFocused(false)}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
              )}

              <button
                type="submit"
                className="lm-primary"
                disabled={isAnyLoading || !canSendOtp}
              >
                {otpSending && <span className="lm-spinner" />}
                {otpSending ? "Sending OTP..." : "Get OTP"}
              </button>
            </form>
          ) : (
            /* OTP Verification Step */
            <form onSubmit={handleVerifyOtp}>
              <div className="lm-otp">
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    id={`modal-otp-${idx}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete={idx === 0 ? "one-time-code" : "off"}
                    placeholder=" "
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpInput(idx, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                    onPaste={handleOtpPaste}
                  />
                ))}
              </div>

              {otpStep && errorMessage ? (
                <div className="lm-otp-error">
                  <AlertCircle size={16} strokeWidth={2.2} />
                  {errorMessage}
                </div>
              ) : (
                <button
                  type="submit"
                  className="lm-primary"
                  style={{ marginBottom: "12px" }}
                  disabled={otpVerifying || !isOtpComplete}
                >
                  {otpVerifying && <span className="lm-spinner" />}
                  {otpVerifying ? "Verifying..." : "Verify & Sign In"}
                </button>
              )}

              <div className="lm-otp-footer">
                <button
                  type="button"
                  className="lm-link"
                  onClick={() => {
                    setOtpStep(false);
                    setOtp(["", "", "", "", "", ""]);
                    setErrorMessage("");
                  }}
                >
                  <ArrowLeft size={14} strokeWidth={2.2} />
                  Change {authMode === "phone" ? "Number" : "Email"}
                </button>

                {canResend ? (
                  <button
                    type="button"
                    className="lm-link"
                    onClick={handleSendOtp}
                  >
                    Resend OTP
                  </button>
                ) : (
                  <span className="lm-muted">Resend in {timer}s</span>
                )}
              </div>
            </form>
          )}

          {/* Guest dismissal footer */}
          {canDismiss && (
            <div className="lm-footer">
              <button type="button" onClick={onClose}>
                Stay logged out
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default LoginModal;
